import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAtom, useSetAtom, useStore } from 'jotai';
import { useAuth } from '@context';
import { useRefreshAuthTokens } from '@hooks';
import { UserRoleService } from '@services';
import {
  getIsTokenExpired,
  getLocalAccessToken,
  getLocalTokenExpiry,
  randomId,
} from '@utils';
import type { ImportedNetworkRatecard } from '../../store/jotai';
import {
  importedNetworkRatecardAtom,
  networkRatecardEditorDirtyAtom,
} from '../../store/jotai';
import { analysedImportIdsAtom, wingmanOutboxAtom } from '../../store/wingman';

const HANDSHAKE_TIMEOUT_MS = 8000;
const MAX_TOKEN_EXPIRED_CYCLES = 3;
const NATURAL_EXPIRY_RESET_MS = 60_000;
const MAX_TRACKED_ANALYSED_IMPORTS = 50;

/* FLOX refresh tokens are ONE-TIME-USE and rotate on every refresh. Two embed instances can be
   live at once (the full-page board plus the opened slide-out panel) and both hear their own
   iframe's 'wingman-token-expired' at the same moment — without this module-level dedup each
   would POST the same refresh token, the loser's refresh would 401 and its failure path logs the
   user out, destroying the winner's brand-new session (review 2026-07-21). */
let inflightTokenRefresh: Promise<unknown> | null = null;
const refreshAuthTokensOnce = (
  refreshAuthTokens: () => Promise<unknown>
): Promise<unknown> => {
  if (!inflightTokenRefresh) {
    inflightTokenRefresh = refreshAuthTokens().finally(() => {
      inflightTokenRefresh = null;
    });
  }
  return inflightTokenRefresh;
};

export type WingmanEmbedStatus = 'connecting' | 'ready' | 'unavailable';

interface WingmanEmbedOptions {
  panel?: boolean;
  active?: boolean;
}

const hasParseableExpiry = (
  tokenExpiry: string | null
): tokenExpiry is string =>
  !!tokenExpiry && !Number.isNaN(new Date(tokenExpiry).getTime());

/* The FLOX side of the Wingman postMessage bridge, shared by the full-page WingmanBoard and
   the global WingmanPanel slide-out. The embed targets the SIDE dashboard (/wingman/shipper |
   /wingman/lsp) — the bare /wingman root is the OPERATOR board, whose instance-scoped widgets
   answer tenants with honest empties and 403s by design. FLOX is the sole dispenser of the
   tenant JWT: the shell announces itself with 'wingman-ready', we post { type: 'flox-auth',
   token, theme, displayName }, and on 'wingman-token-expired' we refresh and re-post. The
   token only ever travels over postMessage, pinned to our origin. `active` gates the
   handshake timeout so a lazily-mounted iframe (the panel before first open) is never marked
   unavailable while it simply isn't rendered. */
export function useWingmanEmbed({
  panel = false,
  active = true,
}: WingmanEmbedOptions = {}) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const isRefreshingTokenRef = useRef(false);
  const tokenExpiredCyclesRef = useRef(0);
  const lastTokenExpiredAtRef = useRef(0);
  const { user } = useAuth();
  const { isWarehouseClient } = UserRoleService();
  const { refreshAuthTokens } = useRefreshAuthTokens();
  const [embedStatus, setEmbedStatus] =
    useState<WingmanEmbedStatus>('connecting');
  const [embedAttempt, setEmbedAttempt] = useState(0);
  const [wingmanOutbox, setWingmanOutbox] = useAtom(wingmanOutboxAtom);
  const setAnalysedImportIds = useSetAtom(analysedImportIdsAtom);
  const store = useStore();
  const navigate = useNavigate();

  const sideBase = isWarehouseClient(user)
    ? '/wingman/shipper'
    : '/wingman/lsp';
  const embedUrl = `${sideBase}?embedded=1${panel ? '&panel=1' : ''}`;

  const postAuthToWingman = useCallback(async () => {
    const tokenExpiry = getLocalTokenExpiry();
    if (
      hasParseableExpiry(tokenExpiry) &&
      getIsTokenExpired(new Date(), new Date(tokenExpiry))
    ) {
      await refreshAuthTokensOnce(refreshAuthTokens);
    }
    const accessToken = getLocalAccessToken();
    const wingmanWindow = iframeRef.current?.contentWindow;
    if (!accessToken || !wingmanWindow) return;
    wingmanWindow.postMessage(
      {
        type: 'flox-auth',
        token: accessToken,
        theme: 'light',
        displayName: user?.name,
      },
      window.location.origin
    );
  }, [refreshAuthTokens, user]);

  /* Each 'wingman-token-expired' is answered with a refresh + re-post, but only a bounded
     number of times per shell instance: a Wingman that keeps rejecting valid FLOX tokens
     (e.g. a JWT-verifier misconfig) would otherwise drive an endless refresh loop that
     rotates the refresh token and can race other tabs into a full FLOX logout. */
  const refreshThenRepostAuth = useCallback(async () => {
    if (isRefreshingTokenRef.current) return;
    /* Rapid-fire expiries mean Wingman is rejecting our tokens (verifier misconfig) — that loop
       stays bounded. A fresh expiry after a quiet spell is just natural token rotation on a
       long-lived tab, so the bound resets instead of marking a healthy embed unavailable on its
       fourth rotation (review 2026-07-21). */
    const now = Date.now();
    if (now - lastTokenExpiredAtRef.current > NATURAL_EXPIRY_RESET_MS) {
      tokenExpiredCyclesRef.current = 0;
    }
    lastTokenExpiredAtRef.current = now;
    if (tokenExpiredCyclesRef.current >= MAX_TOKEN_EXPIRED_CYCLES) {
      setEmbedStatus('unavailable');
      return;
    }
    tokenExpiredCyclesRef.current += 1;
    isRefreshingTokenRef.current = true;
    try {
      await refreshAuthTokensOnce(refreshAuthTokens);
      await postAuthToWingman();
    } finally {
      isRefreshingTokenRef.current = false;
    }
  }, [postAuthToWingman, refreshAuthTokens]);

  useEffect(() => {
    const handleWingmanMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.source !== iframeRef.current?.contentWindow) return;
      const message = event.data as { type?: string } | null;
      if (message?.type === 'wingman-ready') {
        tokenExpiredCyclesRef.current = 0;
        setEmbedStatus('ready');
        postAuthToWingman();
      }
      if (message?.type === 'wingman-token-expired') {
        refreshThenRepostAuth();
      }
      if (message?.type === 'wingman-import-analysed') {
        const { importId } = event.data as { importId?: string };
        if (importId) {
          setAnalysedImportIds((analysedIds) =>
            analysedIds.includes(importId)
              ? analysedIds
              : [...analysedIds, importId].slice(-MAX_TRACKED_ANALYSED_IMPORTS)
          );
        }
      }
      if (message?.type === 'wingman-ratecard-import') {
        const { ratecard } = event.data as {
          ratecard?: Omit<ImportedNetworkRatecard, 'id'>;
        };
        if (ratecard && Array.isArray(ratecard.zones)) {
          /* The ack goes back BEFORE the dirty-check and the atom write. It means "this
             message type is handled", nothing more — the Wingman panel tells the user the
             card was sent only on hearing it, and a declined dirty-check is a visible choice
             in this window that needs no second signal. The source is already pinned to our
             iframe, so posting via the ref reaches exactly the emitter. */
          iframeRef.current?.contentWindow?.postMessage(
            { type: 'wingman-ratecard-import-ack' },
            window.location.origin
          );
          const canOverwrite =
            !store.get(networkRatecardEditorDirtyAtom) ||
            // Deliberate native confirm: this hook renders nothing, so the Copilot-era
            // modal isn't available here, and the check must block synchronously inside
            // the message handler.
            // eslint-disable-next-line no-alert
            window.confirm(
              'Replace the rate card you are editing with the imported one?'
            );
          if (canOverwrite) {
            store.set(importedNetworkRatecardAtom, {
              ...ratecard,
              // Fresh per import — AddNetworkRatecard keys the editor on it, so a re-import
              // remounts with the new values even when already on /network-ratecard/add.
              id: randomId(),
              zones: ratecard.zones.map((zone) => ({
                ...zone,
                // Source-format token order is semantic (prefix carry-forward is
                // positional) — joined verbatim, never sorted or expanded here.
                originalPostcodesInput: zone.postcodes.join(', '),
              })),
            });
            navigate('/network-ratecard/add');
          }
        }
      }
    };
    window.addEventListener('message', handleWingmanMessage);
    return () => window.removeEventListener('message', handleWingmanMessage);
  }, [
    postAuthToWingman,
    refreshThenRepostAuth,
    setAnalysedImportIds,
    store,
    navigate,
  ]);

  /* Import-analysis requests queue in a shared outbox atom so any surface (upload modal, review
     view) can hand work to whichever Wingman iframe is mounted. Both Wingman surfaces (full-page
     board + slide-out panel) can be mounted at once, so the drain CLAIMS the queue inside the
     atom updater — only the first surface to run sees a non-empty queue and posts, the rest see
     the emptied queue and no-op, preventing a duplicate analysis run. Delivery is at-least-once
     and only an optimisation: the review view polls regardless, and a message that never drains
     (no iframe mounted) is harmless. */
  useEffect(() => {
    if (embedStatus !== 'ready' || wingmanOutbox.length === 0) return;
    const wingmanWindow = iframeRef.current?.contentWindow;
    if (!wingmanWindow) return;
    let claimed: typeof wingmanOutbox = [];
    setWingmanOutbox((queued) => {
      claimed = queued;
      return queued.length === 0 ? queued : [];
    });
    claimed.forEach((message) => {
      wingmanWindow.postMessage(message, window.location.origin);
    });
  }, [embedStatus, wingmanOutbox, setWingmanOutbox]);

  useEffect(() => {
    if (!active || embedStatus !== 'connecting') return undefined;
    const handshakeTimeout = setTimeout(
      () => setEmbedStatus('unavailable'),
      HANDSHAKE_TIMEOUT_MS
    );
    return () => clearTimeout(handshakeTimeout);
  }, [active, embedStatus, embedAttempt]);

  const retryConnection = useCallback(() => {
    setEmbedStatus('connecting');
    setEmbedAttempt((attempt) => attempt + 1);
  }, []);

  return { iframeRef, embedStatus, embedUrl, embedAttempt, retryConnection };
}
